/**
 * The TV on the screen (owner, Sep 25: "new action: watch tv"): while the local player watches (`player.tv`, game/tv.ts),
 * a TV set fills the middle of the view — the screen with the channel's clip, the number and the channel's name in the
 * corner after each change, a burst of static between channels — and at the end the screen collapses to a dot, the set
 * fades and the character says the line (a bubble over their head, which everyone in the room sees: `tvBubbles`).
 *
 * A channel whose clip has every part rendered (docs/DESIGN_CLIPS.md) plays on Tock's `ClipPlayer` — the shots, the voice-over,
 * the music, the sounds, the captions — fed by the same host as the phone's app (the public media index, online or the
 * local one). Until then the **placeholder rule** of docs/DESIGN_MEDIA.md: a card with the clip's title and line on the
 * channel's colours, under a little moving grain, while the browser voice reads the narration — so the whole thing works
 * before a single render.
 */
import type { Registry } from '../content/registry';
import type { Media } from '../content/types';
import type { Person } from './entities';
import { speech } from '../engine/speech';
import type { ClipPlan } from './clips';
import { hashStr } from '../world/rng';
import { clipPlan } from './clips';
import { ClipPlayer, type TockHost } from './tock';
import { planFor, surfAt, tvClipsOf, LINE_S, TV_LINE, type SurfPlan, type TvClip, type TvWatch } from './tv';

export interface TvHost {
  reg: Registry;
  time: number;
  player: Person;
  people: Person[];
}
export interface TvHooks {
  /** the Off button: presses E for the player (the server turns it off) */
  off(): void;
  /** the static between channels (a hiss) */
  hiss(): void;
  /** a speech bubble over a person */
  bubble(id: number, text: string): void;
  /** the browser voice may read the narration (the Voices setting) */
  voices(): boolean;
  /** where a clip's rendered parts are and how they sound: the phone's Tock host (main.ts) — the TV plays on its `ClipPlayer` */
  clips: TockHost;
}

const clips = (reg: Registry): TvClip[] => tvClipsOf(reg);

interface View { root: HTMLDivElement; set: HTMLDivElement; stage: HTMLDivElement; player: ClipPlayer; canvas: HTMLCanvasElement; osd: HTMLDivElement; cap: HTMLDivElement; hint: HTMLDivElement }
let view: View | undefined;
/** what is on: the surf (by seed) and the step the screen is showing, so a change is noticed once */
let on: { seed: number; shown: string; spoke: string; hissed: number; osdUntil: number } | undefined;

function build(hooks: TvHooks): View {
  const root = document.createElement('div');
  root.id = 'tv';
  root.style.cssText = 'position:fixed;inset:0;display:none;align-items:center;justify-content:center;flex-direction:column;background:rgba(6,8,12,0.55);z-index:29;pointer-events:none;transition:opacity .6s';
  const set = document.createElement('div');
  set.style.cssText = 'width:min(90vw,122vh);background:linear-gradient(#24252b,#141519);border-radius:1.6vmin;padding:1.4vmin 1.4vmin 2.6vmin;box-shadow:0 18px 60px rgba(0,0,0,.7),inset 0 1px 0 rgba(255,255,255,.08);position:relative;box-sizing:border-box';
  const screen = document.createElement('div');
  screen.style.cssText = 'position:relative;width:100%;aspect-ratio:16/9;background:#000;border-radius:1vmin;overflow:hidden';
  // the rendered channel: Tock's stage (two shot videos taking turns, the head) under the TV's own card, rules like #tock's
  const stage = document.createElement('div'); stage.className = 'tk-stage'; stage.style.display = 'none';
  stage.innerHTML = '<video class="tk-shot a" muted playsinline preload="auto"></video><video class="tk-shot b" muted playsinline preload="auto"></video><video class="tk-head" playsinline preload="auto"></video>';
  const css = document.createElement('style');
  css.textContent = '#tv .tk-stage,#tv .tk-stage video{position:absolute;inset:0;width:100%;height:100%}#tv .tk-stage video{object-fit:cover;background:#000;opacity:0;transition:opacity .12s}#tv .tk-stage.shots .tk-shot.on,#tv .tk-head.on{opacity:1}#tv .tk-stage[data-layout=head] .tk-shot,#tv .tk-stage[data-layout=cut].shots .tk-head{opacity:0!important}#tv .tk-stage[data-layout=react] .tk-head{inset:auto;left:3%;top:6%;width:24%;height:auto;aspect-ratio:1;border-radius:50%;border:.2em solid #fff;z-index:2}#tv .tk-stage[data-layout=split] .tk-shot{height:50%}#tv .tk-stage[data-layout=split] .tk-head{top:50%;height:50%}';
  root.append(css);
  const canvas = document.createElement('canvas'); canvas.width = 640; canvas.height = 360; canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%';
  const osd = document.createElement('div'); osd.style.cssText = 'position:absolute;top:4%;right:4%;text-align:right;font:bold clamp(14px,3.4vmin,34px)/1.1 ui-monospace,Consolas,monospace;color:#6cff7a;text-shadow:0 0 6px rgba(80,255,110,.8),2px 2px 0 #000;opacity:0;transition:opacity .3s';
  const cap = document.createElement('div'); cap.style.cssText = 'position:absolute;left:6%;right:6%;bottom:5%;text-align:center;font:600 clamp(12px,2.2vmin,20px)/1.35 system-ui,sans-serif;color:#fff;text-shadow:0 1px 3px #000,0 0 2px #000';
  screen.append(stage, canvas, osd, cap);
  const led = document.createElement('div'); led.style.cssText = 'position:absolute;right:3%;bottom:0.9vmin;width:0.8vmin;height:0.8vmin;border-radius:50%;background:#ff3b30;box-shadow:0 0 6px #ff3b30';
  set.append(screen, led);
  const bar = document.createElement('div'); bar.style.cssText = 'margin-top:1.2vmin;display:flex;gap:1.2em;align-items:center;font:13px system-ui,sans-serif;color:#c8d4e0;text-shadow:0 1px 2px #000';
  const hint = document.createElement('div'); hint.textContent = 'E, F or a step turns it off';
  const btn = document.createElement('button'); btn.textContent = '⏻ Off'; btn.style.cssText = 'pointer-events:auto;padding:.5em 1.2em;border-radius:2em;border:0;background:#8a2a2a;color:#fff;font:bold 14px system-ui,sans-serif;cursor:pointer';
  btn.addEventListener('pointerdown', (e) => { e.preventDefault(); hooks.off(); });
  btn.addEventListener('pointerup', (e) => (e.currentTarget as HTMLElement).blur());
  bar.append(hint, btn);
  root.append(set, bar);
  document.body.append(root);
  return { root, set, stage, player: new ClipPlayer(stage, hooks.clips), canvas, osd, cap, hint };
}

/** the TV is on for this browser's player (main.ts ducks the world's sound meanwhile) */
export function tvOn(): boolean { return !!on; }

/** every frame: the local player's TV, and the line over the heads of everyone whose surf has run out */
export function tvFrame(state: TvHost, hooks: TvHooks, touch: boolean): void {
  tvBubbles(state, hooks);
  const w = state.player.tv;
  if (!w || w.game || state.player.dead) { if (on) close(); return; } // (owner, Sep 28: the console on the set is the Arcade's — arcade-view.ts)
  if (typeof document === 'undefined') return;
  view ??= build(hooks);
  const list = clips(state.reg), plan = planFor(w, list), t = state.time - w.at, now = surfAt(plan, t);
  if (!on || on.seed !== w.seed) { on = { seed: w.seed, shown: '', spoke: '', hissed: -1, osdUntil: 0 }; view.root.style.display = 'flex'; view.root.style.opacity = '1'; }
  view.hint.textContent = touch ? 'Tap Off, TV OFF or walk away' : 'E, F or a step turns it off';
  if (now.phase === 'off') { close(); return; }
  if (now.phase === 'line') { lineOff(view, plan, t); return; }
  const step = plan.steps[now.step], m = state.reg.get('media', step.clip);
  if (now.phase === 'static') {
    if (on.hissed !== now.step) { on.hissed = now.step; hooks.hiss(); stopClip(view); speech.stop(); on.shown = ''; }
    drawStatic(view.canvas); view.canvas.style.display = 'block'; view.cap.textContent = '';
    return;
  }
  const key = `${now.step}:${step.clip}`;
  if (on.shown !== key) {
    on.shown = key; on.osdUntil = performance.now() + 2600;
    view.osd.innerHTML = `CH ${String(step.channel).padStart(2, '0')}<div style="font-size:.55em;margin-top:.2em">${esc(m?.from ?? '')}</div>`;
    stopClip(view); view.cap.textContent = '';
    const full = m ? clipPlan(m, { part: (stem) => hooks.clips.part(stem), song: (id) => hooks.clips.song(id) }) : undefined, plan = full?.ready ? full : undefined;
    if (plan && m) { view.player.play(plan, m); view.stage.style.display = 'block'; view.canvas.style.display = 'none'; playing = true; }
    else { view.canvas.style.display = 'block'; if (full?.voice) cardVoice(full.voice.src, hooks); } // (owner, Sep 28: the card with the advert's recorded voice, not the browser's)
  }
  view.osd.style.opacity = performance.now() < on.osdUntil ? '1' : '0';
  if (playing) { view.player.frame(); view.cap.textContent = view.player.caption(); } // (the shots, the voice, the music and the sounds on the player's clock; its burned-in captions)
  else if (m) {
    drawCard(view.canvas, state.reg, m, now.clipT, step.secs);
    // the placeholder's voice: the narration, read once per channel by the browser voice
    if (on.spoke !== key) { on.spoke = key; const words = narration(m); if (words && !cardAudio?.src && hooks.voices() && (hooks.clips.fallback?.() ?? true)) speech.say(words, voiceOf(m, state.reg), 0.9, true); } // (only a line with no recording, where the world lets the browser read)
    view.cap.textContent = captionAt(m, now.clipT);
  } else if (!m) drawStatic(view.canvas);
}

/** the end: the picture collapses to a line and a dot, the set fades out, the character's words stay in the bubble */
function lineOff(v: View, plan: SurfPlan, t: number): void {
  if (on && on.shown !== 'line') { on.shown = 'line'; stopClip(v); speech.stop(); v.osd.style.opacity = '0'; v.cap.textContent = ''; }
  const k = t - plan.lineAt, ctx = v.canvas.getContext('2d'); v.canvas.style.display = 'block';
  if (ctx) {
    const W = v.canvas.width, H = v.canvas.height; ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);
    const a = Math.min(1, k / 0.35); // (0 → 1 over a third of a second)
    const hh = Math.max(2, H * (1 - a) * 0.5), ww = a < 0.7 ? W : W * Math.max(0.01, (1 - a) / 0.3);
    if (k < 0.6) { ctx.fillStyle = `rgba(255,255,255,${1 - Math.max(0, k - 0.35) / 0.25})`; ctx.fillRect(W / 2 - ww / 2, H / 2 - hh / 2, ww, Math.min(hh, 6 + (1 - a) * H)); }
    ctx.fillStyle = 'rgba(230,236,255,0.85)'; ctx.font = 'italic 600 26px system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    if (k > 0.5) { ctx.globalAlpha = Math.min(1, (k - 0.5) / 0.3); ctx.fillText(`“${TV_LINE}”`, W / 2, H / 2); ctx.globalAlpha = 1; }
  }
  v.root.style.opacity = k > LINE_S - 1.2 ? '0' : '1'; // (the set fades so the bubble over the character is seen)
}

function close(): void {
  on = undefined; if (!view) return;
  stopClip(view); speech.stop(); view.root.style.display = 'none'; view.osd.style.opacity = '0';
}

/** a channel's clip when every part of it is rendered (its plan, src/game/clips.ts), else nothing: the placeholder card, as the rule
 * says — a half-rendered advert is worse than the card */
export function renderedOf(h: Pick<TockHost, 'part' | 'song'>, m: Media): ClipPlan | undefined {
  const plan = clipPlan(m, { part: (stem) => h.part(stem), song: (id) => h.song(id) });
  return plan.ready ? plan : undefined;
}
let playing = false;
function stopClip(v: View): void { if (playing) { v.player.stop(); playing = false; } v.stage.style.display = 'none'; if (cardAudio) { cardAudio.pause(); cardAudio.removeAttribute('src'); } }
/** (Sep 28) a channel shown as its card (a part is not rendered yet) still speaks with its recorded voice when it has one */
let cardAudio: HTMLAudioElement | undefined;
function cardVoice(src: string, hooks: TvHooks): void { cardAudio ??= new Audio(); cardAudio.src = src; cardAudio.volume = Math.max(0, Math.min(1, hooks.clips.volume())); void cardAudio.play().catch(() => undefined); }

/** the line over the head of anyone (me included) whose surf has just run out — once per surf */
const bubbled = new Set<number>();
function tvBubbles(state: TvHost, hooks: TvHooks): void {
  for (const p of state.people) {
    const w: TvWatch | undefined = p.tv; if (!w || w.game || p.dead) continue;
    if (bubbled.has(w.seed)) continue;
    const plan = planFor(w, clips(state.reg)); if (state.time - w.at < plan.lineAt || state.time - w.at > plan.total) continue;
    bubbled.add(w.seed); if (bubbled.size > 200) bubbled.delete(bubbled.values().next().value!);
    hooks.bubble(p.id, TV_LINE);
  }
}

/** the words the browser voice reads for a clip with no take: the voice-over lines */
export function narration(m: Media): string {
  return (m.line ?? '').replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim();
}
/** the caption under the placeholder card: the narration's sentence for this moment of the clip */
function captionAt(m: Media, t: number): string {
  const words = narration(m); if (!words) return '';
  const parts = words.match(/[^.!?]+[.!?]*/g)?.map((s) => s.trim()).filter(Boolean) ?? [words];
  const secs = Math.max(1, m.seconds ?? 20), i = Math.min(parts.length - 1, Math.floor((t / secs) * parts.length));
  return parts[i];
}
/** a narrator for a nature programme is slow and low; an advert is quick and bright; the rest in between */
function voiceOf(m: Media, reg: Registry): { sex: 'f' | 'm'; pitch: number; rate: number; seed: number } {
  const c = m.character ? reg.get('character', m.character) : undefined; // (the clip's voice: a narrator's, an announcer's — its browser-voice settings)
  if (c) return { sex: c.sex, pitch: c.voice?.pitch ?? 1, rate: c.voice?.rate ?? 1, seed: hashStr(c.id) };
  const seed = hashStr(m.from ?? m.id), g = genreOf(m);
  if (g === 'nature') return { sex: 'm', pitch: 0.8, rate: 0.85, seed };
  if (g === 'commercial') return { sex: seed % 2 ? 'f' : 'm', pitch: 1.15, rate: 1.15, seed };
  return { sex: seed % 2 ? 'f' : 'm', pitch: 1, rate: 1, seed };
}
export function genreOf(m: Media): 'commercial' | 'nature' | 'news' | 'cooking' | 'show' {
  for (const g of ['commercial', 'nature', 'news', 'cooking'] as const) if (m.tags.includes(g)) return g;
  return 'show';
}

const GENRE: Record<string, { bg: [string, string]; label: string }> = {
  commercial: { bg: ['#b8401c', '#f4d070'], label: 'ADVERTISEMENT' },
  nature: { bg: ['#1f5a3a', '#8fc46a'], label: 'NATURE' },
  news: { bg: ['#12305c', '#3a78c8'], label: 'NEWS' },
  cooking: { bg: ['#8a3a14', '#f0a040'], label: 'COOKING' },
  show: { bg: ['#3a2a6a', '#b070d0'], label: 'ON NOW' },
};
/** the placeholder card: the channel's colours (an advert's brand colours), the kind, the title, the line, a moving grain */
function drawCard(cv: HTMLCanvasElement, reg: Registry, m: Media, t: number, secs: number): void {
  const ctx = cv.getContext('2d'); if (!ctx) return;
  const W = cv.width, H = cv.height, g = genreOf(m), look = GENRE[g];
  const brand = m.brand ? reg.get('brand', m.brand) : undefined;
  const [c1, c2] = brand ? [brand.color, brand.color2 ?? '#ffffff'] : look.bg;
  const grad = ctx.createLinearGradient(0, 0, W, H); grad.addColorStop(0, c1); grad.addColorStop(1, shade(c1, -0.45)); ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
  // a slow drift of shapes so it reads as moving pictures: hills for nature, stripes for an advert, a ticker for the news
  const drift = (t * 18) % W;
  ctx.globalAlpha = 0.28; ctx.fillStyle = c2;
  if (g === 'nature') { ctx.beginPath(); ctx.moveTo(0, H); for (let x = 0; x <= W; x += 16) ctx.lineTo(x, H * 0.72 - Math.sin((x + drift) / 70) * 26 - Math.sin((x + drift * 0.5) / 23) * 8); ctx.lineTo(W, H); ctx.fill(); }
  else if (g === 'news') { ctx.fillRect(0, H * 0.8, W, H * 0.09); ctx.globalAlpha = 0.8; ctx.fillStyle = '#c8202a'; ctx.fillRect(0, H * 0.8, 90, H * 0.09); }
  else for (let i = -2; i < 8; i++) { ctx.beginPath(); const x = i * 110 + drift; ctx.moveTo(x, 0); ctx.lineTo(x + 50, 0); ctx.lineTo(x - 70, H); ctx.lineTo(x - 120, H); ctx.fill(); }
  ctx.globalAlpha = 1;
  ctx.font = 'bold 14px system-ui, sans-serif'; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
  const lw = ctx.measureText(look.label).width; ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(28, 26, lw + 20, 26); ctx.fillStyle = '#fff'; ctx.fillText(look.label, 38, 39);
  ctx.textAlign = 'center';
  ctx.font = 'bold 36px system-ui, sans-serif'; ctx.lineWidth = 6; ctx.strokeStyle = 'rgba(0,0,0,0.55)'; ctx.fillStyle = '#fff';
  const title = wrap(ctx, m.name, W - 80).slice(0, 2); title.forEach((s, i) => { const y = H * 0.4 + (i - (title.length - 1) / 2) * 42; ctx.strokeText(s, W / 2, y); ctx.fillText(s, W / 2, y); });
  if (m.text) { ctx.font = 'italic 19px system-ui, sans-serif'; ctx.lineWidth = 4; const lines = wrap(ctx, m.text, W - 120).slice(0, 2); lines.forEach((s, i) => { const y = H * 0.58 + i * 25; ctx.strokeText(s, W / 2, y); ctx.fillStyle = '#f2f2f2'; ctx.fillText(s, W / 2, y); }); }
  ctx.font = '11px system-ui, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.textAlign = 'left'; ctx.fillText('a script — not rendered yet', 30, 66); ctx.textAlign = 'center';
  // how far through the part of the clip that plays
  ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.fillRect(0, H - 4, W * Math.min(1, t / Math.max(0.1, secs)), 4);
  grain(ctx, W, H, 0.1);
}
/** the static between channels */
function drawStatic(cv: HTMLCanvasElement): void {
  const ctx = cv.getContext('2d'); if (!ctx) return;
  const W = cv.width, H = cv.height; ctx.fillStyle = '#808080'; ctx.fillRect(0, 0, W, H);
  const img = ctx.getImageData(0, 0, W / 4, H / 4), d = img.data;
  for (let i = 0; i < d.length; i += 4) { const v = (Math.random() * 255) | 0; d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 255; }
  ctx.putImageData(img, 0, 0); ctx.imageSmoothingEnabled = false; ctx.drawImage(cv, 0, 0, W / 4, H / 4, 0, 0, W, H);
  ctx.fillStyle = 'rgba(0,0,0,0.25)'; for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);
}
function grain(ctx: CanvasRenderingContext2D, W: number, H: number, a: number): void {
  ctx.fillStyle = `rgba(255,255,255,${a})`; for (let i = 0; i < 260; i++) ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  ctx.fillStyle = 'rgba(0,0,0,0.12)'; for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);
}
function wrap(ctx: CanvasRenderingContext2D, text: string, max: number): string[] {
  const out: string[] = []; let line = '';
  for (const w of text.split(/\s+/)) { const next = line ? `${line} ${w}` : w; if (ctx.measureText(next).width > max && line) { out.push(line); line = w; } else line = next; }
  if (line) out.push(line); return out;
}
function shade(hex: string, k: number): string {
  const n = parseInt(hex.slice(1), 16), f = (c: number) => Math.max(0, Math.min(255, Math.round(c + (k < 0 ? c * k : (255 - c) * k))));
  return `rgb(${f((n >> 16) & 255)},${f((n >> 8) & 255)},${f(n & 255)})`;
}
function esc(s: string): string { return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!); }
